import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import classnames from 'classnames'
import FieldLabelWrapper from '../../../../../../../../../features/components/Form/FieldLabelWrapper'
import Checkbox from '../../../../../../../../../components/Form/Checkbox'
import TiktokCustomAgeRanges from '../../../../../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/Steps/AgesStep/TiktokCustomAgeRanges'
import TikTokLanguages from '../../../../../../../../ReusableFormFields/LanguagesFields/TikTokLanguages'
import ErrorMessage from '../../../../../../../../../components/Form/ErrorMessage'
import MultiSelectBox from '../../../../../../../../../features/components/Form/MultiSelectBox'
import TikTokInterestsMultiLevelSelect from '../../../../../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/Steps/InterestsStep/TikTokInterestsMultiLevelSelect'
import MessageBlock from '../../../../../../../../../features/components/Form/MessageBlock'
import AdditionalInterestCategoriesMultiselect, {
  INTEREST_KEYWORD_IDS
} from './AdditionalInterestCategoriesMultiselect'
import PurchaseIntentionCategoriesMultiselect, {
  PURCHASE_INTENTION_KEYWORD_IDS
} from './PurchaseIntentionCategoriesMultiselect'

import { getShowPurchaseIntentionCategories } from './helpers'

import { tikTokCustomAudiencesIsLoadingSelector } from '../../../../../../../../../modules/selectors/tikTokConstants'
import { tiktokRightUploadAdDataSelector } from '../../../../../../../../../modules/selectors/rights'

import {
  GENDER_FEMALE,
  GENDER_MALE
} from '../../../../../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/fields'
import { LANGUAGES } from '../../../../../../../../ReusableFormFields/LanguagesFields/fields'
import { CUSTOM_AUDIENCES_EXCLUSION } from '../../../../../../../../ReusableFormFields/Audiences/AudiencesFields/fields'

import { INTERESTS } from '../../../../../../../../ReusableFormFields/InterestsFields/fields'

import useDrawerFormStyles from '../../../../../../../../../styles/common/drawerForms'
import useAdvancedAudienceTargetingSectionStyles from '../../../../../../../../../styles/common/styles'

const TikTokAdvancedAudienceTargetingSection = ({ formik, formattedAudiences }) => {
  const { t } = useTranslation()

  const { values, errors, touched, setFieldValue } = formik
  const drawerFormClasses = useDrawerFormStyles()
  const classes = useAdvancedAudienceTargetingSectionStyles()

  const hasInterestKeywords = values[INTEREST_KEYWORD_IDS]?.length > 0
  const hasPurchaseIntentionKeywords = values[PURCHASE_INTENTION_KEYWORD_IDS]?.length > 0

  const tikTokCustomAudiencesIsLoading = useSelector(tikTokCustomAudiencesIsLoadingSelector)

  const { campaign } = useSelector(tiktokRightUploadAdDataSelector)
  const { objective: campaignObjective } = campaign

  const selectedLanguages = values[LANGUAGES]

  const showPurchaseIntentionCategories = useMemo(() => {
    return getShowPurchaseIntentionCategories(campaignObjective)
  }, [campaignObjective])

  const checkboxHandler = useCallback(
    e => {
      const { name } = e.target
      setFieldValue(name, !values[name])
    },
    [values, setFieldValue]
  )

  return (
    <section className={drawerFormClasses.section}>
      <h3 className={classnames(drawerFormClasses.sectionTitle, classes.sectionTitle)}>
        {t('Advanced audience targeting')}
      </h3>

      <FieldLabelWrapper label={t('Exclude an audience')} className={classes.subsection}>
        <MultiSelectBox
          placeholder={t('Select an audience')}
          options={formattedAudiences}
          value={values[CUSTOM_AUDIENCES_EXCLUSION]}
          name={CUSTOM_AUDIENCES_EXCLUSION}
          isLoading={tikTokCustomAudiencesIsLoading}
          setFieldValue={setFieldValue}
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Gender')} className={classes.subsection}>
        <div className={classes.genderCheckboxes}>
          <Checkbox
            id={GENDER_MALE}
            name={GENDER_MALE}
            checked={values[GENDER_MALE]}
            title="Male"
            className={classes.genderCheckbox}
            onCheck={checkboxHandler}
          />
          <Checkbox
            id={GENDER_FEMALE}
            name={GENDER_FEMALE}
            checked={values[GENDER_FEMALE]}
            title="Female"
            className={classes.genderCheckbox}
            onCheck={checkboxHandler}
          />
        </div>
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Ages')} className={classes.subsection}>
        <TiktokCustomAgeRanges formik={formik} isLarge={false} />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Languages')} className={classes.subsection}>
        <TikTokLanguages name={LANGUAGES} setFieldValue={setFieldValue} value={selectedLanguages} />
        {errors[LANGUAGES] && touched[LANGUAGES] && <ErrorMessage error={errors[LANGUAGES]} />}
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('Interests')} className={classes.subsection}>
        <TikTokInterestsMultiLevelSelect formik={formik} />
        {(values[INTERESTS]?.length > 0 ||
          values[INTEREST_KEYWORD_IDS]?.length > 0 ||
          values[PURCHASE_INTENTION_KEYWORD_IDS]?.length > 0) && (
          <MessageBlock title="This will significantly reduce your audience size" borderColor="warning">
            {t(
              'Adding an interest audience will reduce your targeting further and your audience size might end up very small.'
            )}
          </MessageBlock>
        )}
      </FieldLabelWrapper>
      <FieldLabelWrapper label={t('Additional Interest Categories')} className={classes.subsection}>
        <AdditionalInterestCategoriesMultiselect formik={formik} disabled={hasPurchaseIntentionKeywords} />
      </FieldLabelWrapper>
      {showPurchaseIntentionCategories && (
        <FieldLabelWrapper label={t('Purchase Intention Categories')} className={classes.subsection}>
          <PurchaseIntentionCategoriesMultiselect formik={formik} disabled={hasInterestKeywords} />
        </FieldLabelWrapper>
      )}
    </section>
  )
}

export default TikTokAdvancedAudienceTargetingSection
